import useWidth from '@components/Hooks/useWidth';
import FilterBar from '@components/v2/ui/FilterBar/FilterBar';
import SearchField from '@components/v2/ui/FilterBar/components/SearchField/SearchField';
import { Box } from '@mui/material';
import * as R from 'ramda';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { GroupedVirtuoso, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useShallow } from 'zustand/react/shallow';
import { useProductAddedSnackbar } from '../../../components/Product/hooks/useProductAddedSnackbar';
import { useContentFrameScrollParent } from '../../../components/v2/layout/AppTemplate/hooks/useContentFrameScrollParent';
import { ObjectScreenContext } from '../../../components/v2/layout/ObjectScreen/contexts/ObjectScreen.context';
import LangEnum from '../../../lang/LangEnum';
import useProductActionsStore from '../../../stores/ProductActionsStore/productActions.store';
import { getOrderProductUniqueIdentifier } from '../../../utils/getOrderProductUniqueIdentifier';
import { OrderProductsTestIds } from '../../../utils/testIds/OrderProducts.testIds';
import { ProductModalContextProvider } from '../../products/ProductModal.context';
import { useRachelListScroll } from '../../rachel/hooks/useRachelListScroll';
import { OrderProductsProvider, useOrderProductsContext } from '../contexts/OrderProducts.context';
import { OrderProductsLayoutContext } from '../contexts/OrderProductsLayout.context';
import useOrderProductsLayout from '../contexts/hooks/useOrderProductsLayout';
import { useOrderEditSectionsContext } from '../edit/sections/OrderEditSections.context';
import { OrderEditSectionsEnum } from '../edit/sections/OrderEditSections.types';
import CopyOrderProductsToExistingOrderModal from '../modals/CopyOrderProductsToExistingOrderModal/CopyOrderProductsToExistingOrderModal';
import CopyOrderProductsToNewOrderModal from '../modals/CopyOrderProductsToNewOrderModal/CopyOrderProductsToNewOrderModal';
import RemoveOrderProductsFromOrderModal from '../modals/RemoveOrderProductsFromOrderModal/RemoveOrderProductsFromOrderModal';
import { getAddProductRowReservedSpace } from '../orders.utils';
import AddOrderProductArea from './add-product/AddOrderProductArea';
import OrderProductsConditionChip from './components/OrderProductsConditionChip';
import OrderProductsEmptyState from './components/OrderProductsEmptyState/OrderProductsEmptyState';
import OrderProductsTopItemList from './components/OrderProductsTopItemList';
import useOrderProductsConditionFilter from './hooks/useOrderProductsConditionFilter';
import useOrderProductsGridSave from './hooks/useOrderProductsGridSave';
import useOrderProductsMassActions from './hooks/useOrderProductsMassActions/useOrderProductsMassActions';
import useOrderProductsSearch from './hooks/useOrderProductsSearch';
import { OrderHeadersGroupByEnum, OrderProductsProps } from './orderProducts.types';
import {
  getOrderProductSelectionEdge,
  getOrderProductsFilteredBySections,
  getOrderProductsGroupCount,
  getOrderProductsGroupSectionIndexes,
  getOrderProductsGroupStartIndexes,
  resolveOrderProductsEmptyStateKind,
} from './orderProducts.utils';
import OrderProductItem from './product/containers/OrderProductItem/OrderProductItem';
import OrderProductListSkeleton from './product/containers/OrderProductItemSkeleton/OrderProductListSkeleton';
import { GridStoreProvider } from './product/grid/store/GridStoreProvider';
import OrderProductSectionHeader from './section/components/OrderProductSectionHeader/OrderProductSectionHeader';
import { getOrderProductKey } from './section/orderProductsSection.utils';

const OrderProducts = ({
  canEditOrder = false,
  orderBrandHasIntegrations,
  orderId,
  orderStatus,
  viewMode,
  isOfflineMode,
  isAddProductOpen,
  addProductMode,
  onToggleAddProduct,
}: Omit<OrderProductsProps, 'onSuccessFetchProducts'>) => {
  const { isLoading, sections, groupBy, products, hasFetchedProducts } = useOrderProductsContext();

  useProductAddedSnackbar();
  const { currentSection, orderProductsWithErrors } = useOrderEditSectionsContext();
  const {
    conditionSections,
    selectedConditionId,
    selectedConditionLabel,
    conditionOptions,
    selectCondition,
    clearSelectedCondition,
  } = useOrderProductsConditionFilter(sections);
  const { searchText, setSearchText, filteredSections } = useOrderProductsSearch(conditionSections);
  const hasActiveFilter = Boolean(searchText) || Boolean(selectedConditionLabel);

  const handleAfterSave = useOrderProductsGridSave({ isOfflineMode });

  const { scrollToProduct, setScrollToProduct, productNotificationState } = useProductActionsStore(
    useShallow((state) => ({
      scrollToProduct: state.scrollToProduct,
      setScrollToProduct: state.setScrollToProduct,
      productNotificationState: state.productNotificationState,
    })),
  );

  const objectScrollParent = useContext(ObjectScreenContext)?.scrollParent;
  const contentFrameScrollParent = useContentFrameScrollParent();
  const scrollParent = objectScrollParent ?? contentFrameScrollParent;
  const { containerRef, containerWidth, layout, hasTouchGrid, isGridInputReadOnly } =
    useOrderProductsLayout();
  const { smallWidth } = useWidth();
  const orderProducts = getOrderProductsFilteredBySections(filteredSections);
  const {
    selectedItems,
    isAllItemsSelected,
    copyOrderProductsToNewOrderModal,
    copyOrderProductsToExistingOrderModal,
    removeOrderProductsFromOrderModal,
    toggleItems,
    removeItems,
    syncAndToggleItems,
    onClose,
  } = useOrderProductsMassActions({
    products: orderProducts,
    totalOrderProductsCount: products.length,
    isSelectionEnabled: canEditOrder && viewMode !== 'VIEW',
    conditionFilterKey: selectedConditionId ?? '',
  });

  const isGroupedView = groupBy[0] !== OrderHeadersGroupByEnum.DEFAULT;
  const isProductsSectionActive = currentSection === OrderEditSectionsEnum.PRODUCTS;

  const sectionCounts = isProductsSectionActive ? getOrderProductsGroupCount(filteredSections) : [];
  const groupStartIndexes = getOrderProductsGroupStartIndexes(sectionCounts);

  const groupSectionIndexes = getOrderProductsGroupSectionIndexes(filteredSections, isGroupedView);
  const groupCounts = isProductsSectionActive
    ? groupSectionIndexes.map((sectionIndex) => sectionCounts[sectionIndex])
    : [];
  const totalCount = isProductsSectionActive ? orderProducts.length : 0;

  const virtuoso = useRef<VirtuosoHandle>(null);

  const scrollToItem = useCallback((index: number) => {
    virtuoso?.current?.scrollToIndex({
      index,
      behavior: 'smooth',
    });
  }, []);

  useRachelListScroll({
    items: orderProducts,
    getKey: getOrderProductUniqueIdentifier,
    scrollToItem,
  });

  const computeOrderProductKey = (index: number) => {
    const product = orderProducts[index];
    return product ? getOrderProductKey(product) : index;
  };

  const emptyStateKind = resolveOrderProductsEmptyStateKind({
    totalProductsCount: products?.length ?? 0,
    sectionsCount: sections?.length ?? 0,
    visibleProductsCount: orderProducts.length,
    hasActiveFilter,
  });
  const orderHasNoProducts = emptyStateKind === 'order-empty';
  const isFilteredEmptyState = emptyStateKind === 'filter-empty';
  const onClickAddItemFromEmptyState = canEditOrder ? onToggleAddProduct : undefined;
  const isAddProductVisible = !!isAddProductOpen && canEditOrder;
  const isEmptyStateVisible = hasFetchedProducts && !isAddProductVisible && !!emptyStateKind;
  const hasReservedAddProductSpace =
    hasFetchedProducts && orderHasNoProducts && canEditOrder && !smallWidth;

  useEffect(() => {
    if (scrollToProduct) {
      const index = orderProducts.findIndex(
        (op) =>
          op.kit?.id === productNotificationState.kitId ||
          op.productColorId === productNotificationState.productColorId,
      );

      scrollToItem(index);
      setScrollToProduct(false);
    }
  }, [scrollToProduct]);

  const renderOrderProduct = (index: number, sectionIndex: number) => {
    const product = orderProducts[index];
    if (!product) {
      return <></>;
    }

    const orderProductKeyIds = getOrderProductUniqueIdentifier(product);
    const productErrors = orderProductsWithErrors
      ? orderProductsWithErrors[orderProductKeyIds]
      : [];

    const selectionEdge = getOrderProductSelectionEdge({
      productIndex: index,
      orderProducts,
      selectedItems,
      groupStartIndexes,
    });

    return (
      <OrderProductItem
        orderId={orderId}
        orderStatus={orderStatus}
        canEditOrder={canEditOrder}
        key={getOrderProductKey(product)}
        sections={filteredSections}
        sectionIndex={sectionIndex}
        product={product}
        productIndex={index}
        productErrors={productErrors}
        viewMode={viewMode}
        isLastSection={sectionIndex === sections.length - 1}
        orderBrandHasIntegrations={orderBrandHasIntegrations}
        isSelected={selectionEdge !== 'none'}
        selectionEdge={selectionEdge}
        toggleItems={toggleItems}
        removeItems={removeItems}
      />
    );
  };

  return (
    <OrderProductsLayoutContext.Provider
      value={{ layout, containerWidth, hasTouchGrid, isGridInputReadOnly }}
    >
      <Box
        sx={(theme) => ({
          pb: 1,
          marginRight: theme.spacing(1),
          backgroundColor: 'background.paper',
        })}
      >
        <FilterBar
          hasActiveSearch={!!searchText}
          chips={
            selectedConditionLabel ? (
              <OrderProductsConditionChip
                label={selectedConditionLabel}
                selection={{
                  options: conditionOptions,
                  selectedId: selectedConditionId,
                  onSelect: selectCondition,
                  onClear: clearSelectedCondition,
                }}
              />
            ) : undefined
          }
          searchField={
            <SearchField
              value={searchText}
              onChange={setSearchText}
              onClear={() => setSearchText('')}
              placeholder={LangEnum.GLOBAL_SEARCH}
              testId={OrderProductsTestIds.SEARCH_FIELD}
            />
          }
          onClearAll={() => {
            setSearchText('');
            clearSelectedCondition();
          }}
        />
      </Box>
      <GridStoreProvider onAfterSave={handleAfterSave}>
        <Box
          ref={containerRef}
          aria-busy={isLoading}
          sx={(theme) => ({
            position: 'relative',
            ...(isEmptyStateVisible && { display: 'flex', flexDirection: 'column', flex: 1 }),
            ...(hasReservedAddProductSpace && { pt: getAddProductRowReservedSpace(theme) }),
          })}
        >
          <AddOrderProductArea
            isOpen={isAddProductVisible}
            isFloating={hasReservedAddProductSpace}
            orderId={orderId}
            orderStatus={orderStatus}
            mode={addProductMode}
          />

          {isEmptyStateVisible && (
            <OrderProductsEmptyState
              isFiltered={isFilteredEmptyState}
              onClickAddItem={onClickAddItemFromEmptyState}
            />
          )}

          {isLoading && <OrderProductListSkeleton canEditOrder={canEditOrder} />}

          {!isLoading && R.isEmpty(groupCounts) && (
            <Virtuoso
              ref={virtuoso}
              useWindowScroll={!scrollParent}
              customScrollParent={scrollParent ?? undefined}
              overscan={100}
              totalCount={totalCount}
              computeItemKey={computeOrderProductKey}
              itemContent={(index) => renderOrderProduct(index, 0)}
            />
          )}

          {!isLoading && !R.isEmpty(groupCounts) && (
            <GroupedVirtuoso
              ref={virtuoso}
              useWindowScroll={!scrollParent}
              customScrollParent={scrollParent ?? undefined}
              overscan={100}
              groupCounts={groupCounts}
              computeItemKey={computeOrderProductKey}
              components={{ TopItemList: OrderProductsTopItemList }}
              groupContent={(groupIndex) => (
                <OrderProductSectionHeader
                  viewMode={viewMode}
                  canEditOrder={canEditOrder}
                  sectionIndex={groupSectionIndexes[groupIndex]}
                  sections={filteredSections}
                  selectedItems={selectedItems}
                  syncAndToggleItems={syncAndToggleItems}
                />
              )}
              itemContent={(index, groupIndex) =>
                renderOrderProduct(index, groupSectionIndexes[groupIndex])
              }
            />
          )}
        </Box>
      </GridStoreProvider>
      <CopyOrderProductsToNewOrderModal
        isOpen={copyOrderProductsToNewOrderModal.isOpen}
        onClose={(shouldResetMassActions) =>
          onClose(shouldResetMassActions, copyOrderProductsToNewOrderModal.closeModal)
        }
        selectedItems={selectedItems}
        isAllItemsSelected={isAllItemsSelected}
      />
      <CopyOrderProductsToExistingOrderModal
        isOpen={copyOrderProductsToExistingOrderModal.isOpen}
        onClose={(shouldResetMassActions) =>
          onClose(shouldResetMassActions, copyOrderProductsToExistingOrderModal.closeModal)
        }
        selectedItems={selectedItems}
        isAllItemsSelected={isAllItemsSelected}
      />
      <RemoveOrderProductsFromOrderModal
        isOpen={removeOrderProductsFromOrderModal.isOpen}
        onClose={(shouldResetMassActions) =>
          onClose(shouldResetMassActions, removeOrderProductsFromOrderModal.closeModal)
        }
        selectedItems={selectedItems}
        isAllItemsSelected={isAllItemsSelected}
      />
    </OrderProductsLayoutContext.Provider>
  );
};

const OrderProductsContext = (props: OrderProductsProps) => {
  return (
    <OrderProductsProvider isOfflineMode={props.isOfflineMode}>
      <ProductModalContextProvider>
        <OrderProducts {...props} />
      </ProductModalContextProvider>
    </OrderProductsProvider>
  );
};

export default OrderProductsContext;
